/**
 * ATS match score: weighted coverage of job keywords by resume keywords.
 * Boost ceiling = deliverable soft misses only (soft_skill | responsibility | domain
 * that we can weave naturally from resume evidence).
 * Hard gaps (technology | programming_language | skill | education | certification |
 * language | job_title) and non-deliverable domains are never promised by Boost.
 */

import { isIgnoredAtsKeyword } from "./atsKeywordPolicy.js";
import {
  filterItemsByNormalized,
  weakMatchKeys,
} from "./keywordSubsume.js";
import { isFranchiseCompatible } from "./businessLines.js";
import { resumeHasExactKeywordEvidence } from "./exactMatchEvidence.js";
import { BOOSTABLE_CATEGORIES, normalizeKeywordCategory, SKILLS_CHIP_CATEGORIES } from "./keywordCategories.js";

/**
 * Asset classes / product labels — often tagged `domain`, but Boost must NOT promise
 * them: weaving "ETFs" / "Private credit" without source evidence is invention.
 */
const NON_BOOSTABLE_DOMAIN_KEYS = new Set(
  [
    "etf",
    "etfs",
    "mutual funds",
    "mutual fund",
    "private credit",
    "private equity",
    "real estate",
    "real estate investment",
    "high yield",
    "fx",
    "forex",
    "foreign exchange",
    "investment products",
    "investment product",
    "equities",
    "equity products",
    "fixed income products",
    "commodities",
    "alternatives",
    "hedge funds",
    "hedge fund",
    "money market",
    "municipal bonds",
    "abs",
    "clo",
    "cdo",
    "global loans and special situations",
    "global loans special situations",
    "loans and special situations",
    "special situations",
    "combine",
    // Job titles / tracks — cannot weave without looking invented.
    "financial controller",
    "controller",
    // Buzz domains with no honest evidence path for most finance resumes.
    "artificial intelligence",
    "machine learning",
    "generative ai",
    "gen ai",
  ].map((s) => s.toLowerCase()),
);

const NON_BOOSTABLE_DOMAIN_RE =
  /\b(etfs?|mutual funds?|private (credit|equity)|real estate|high yield|hedge funds?|money market|municipal|special situations|investment products?|asset class|product suite|artificial intelligence|machine learning|generative ai)\b/i;

/** Stems too common alone to prove domain fit. */
export const WEAK_WEAVE_STEMS = new Set([
  "financial",
  "finance",
  "business",
  "data",
  "management",
  "risk",
  "analysis",
  "investment",
  "market",
  "markets",
  "company",
  "client",
  "clients",
  "portfolio",
  "research",
  "model",
  "models",
  "modeling",
]);

/**
 * @param {string} phrase
 * @returns {string[]}
 */
export function weaveStems(phrase) {
  return String(phrase || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(
      (w) =>
        w.length > 3 && !["skills", "skill", "and", "the", "with", "from", "for"].includes(w),
    );
}

/**
 * @param {string[]} stems
 * @param {string} textLower
 */
export function stemFitScore(stems, textLower) {
  const hay = String(textLower || "").toLowerCase();
  let strong = 0;
  let weak = 0;
  for (const s of stems || []) {
    if (!hay.includes(s)) continue;
    if (WEAK_WEAVE_STEMS.has(s)) weak += 1;
    else strong += 1;
  }
  if (strong >= 1) return strong * 2 + weak;
  if (weak >= 2) return weak;
  return 0;
}

/**
 * Soft / rephraseable category miss (before deliverability filter).
 * @param {{ keyword?: string, keyword_normalized?: string, category?: string }} item
 */
export function isBoostableKeyword(item) {
  const category = normalizeKeywordCategory(item?.category);
  if (!BOOSTABLE_CATEGORIES.has(category)) return false;

  const key = String(item?.keyword_normalized || item?.keyword || "")
    .trim()
    .toLowerCase();
  if (!key) return false;
  if (NON_BOOSTABLE_DOMAIN_KEYS.has(key)) return false;
  if (category === "domain" && NON_BOOSTABLE_DOMAIN_RE.test(key)) return false;
  return true;
}

/**
 * Can post-processor weave this phrase naturally from resume evidence?
 * soft_skill → always (summary).
 * domain/responsibility → stem fit + franchise/business-line compatibility.
 * @param {{ keyword?: string, keyword_normalized?: string, category?: string }} item
 * @param {string} [evidenceText]
 */
export function isWeaveDeliverable(item, evidenceText = "") {
  if (!isBoostableKeyword(item)) return false;
  const category = String(item?.category || "")
    .trim()
    .toLowerCase();
  if (category === "soft_skill") return true;

  const phrase = String(item?.keyword || item?.keyword_normalized || "").trim();
  if (!phrase) return false;
  const text = String(evidenceText || "").trim();
  // No evidence → do not promise domains (avoids inventing franchise labels).
  if (!text) return false;
  if (!isFranchiseCompatible(phrase, text)) return false;
  return stemFitScore(weaveStems(phrase), text.toLowerCase()) >= 1;
}

/**
 * @deprecated Ceiling is honest again via dictionary categories — return raw boost %.
 * @param {number} currentPercent
 * @param {number} boostPercent
 */
export function advertisedBoostPercent(currentPercent, boostPercent) {
  const cur = Number(currentPercent);
  const raw = Number(boostPercent);
  if (!Number.isFinite(cur)) return 0;
  if (!Number.isFinite(raw) || raw <= cur) return Math.max(0, Math.min(100, Math.round(cur)));
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/**
 * @typedef {{ keyword?: string, keyword_normalized?: string, category?: string, weight?: number, matched_pattern?: string, resume_match?: KeywordItem | null }} KeywordItem
 *
 * @typedef {{
 *   currentPercent: number,
 *   boostPercent: number,
 *   band: 'weak' | 'medium' | 'strong',
 *   matched: KeywordItem[],
 *   missingHard: KeywordItem[],
 *   boostable: KeywordItem[],
 *   totalWeight: number,
 *   matchedCount: number,
 *   matchedHardCount: number,
 *   matchedWeight: number,
 *   jobKeywordCount: number,
 * }} MatchScore
 */

/**
 * @param {KeywordItem[] | null | undefined} items
 * @returns {Map<string, KeywordItem>}
 */
function indexByNormalized(items) {
  const map = new Map();
  for (const item of items || []) {
    const key = String(item?.keyword_normalized || "")
      .trim()
      .toLowerCase();
    if (!key) continue;
    if (!map.has(key)) map.set(key, item);
  }
  return map;
}

/**
 * @param {Map<string, KeywordItem>} resumeIndex
 * @param {string} jobKey
 * @returns {KeywordItem | null}
 */
function findResumeMatch(resumeIndex, jobKey) {
  if (resumeIndex.has(jobKey)) return resumeIndex.get(jobKey) || null;
  for (const [resumeKey, resumeItem] of resumeIndex.entries()) {
    if (weakMatchKeys(resumeKey, jobKey)) return resumeItem;
  }
  return null;
}

/**
 * @param {KeywordItem} item
 */
function itemWeight(item) {
  const w = Number(item?.weight);
  if (!Number.isFinite(w)) return 2;
  return Math.min(4, Math.max(1, w));
}

/**
 * Largest-remainder percents so integer contributions sum to 100.
 * @param {KeywordItem[]} items
 * @param {number} totalWeight
 * @returns {Array<KeywordItem & { contributionPercent: number }>}
 */
export function distributeContributionPercents(items, totalWeight) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return [];
  const total = Number(totalWeight);
  if (!Number.isFinite(total) || total <= 0) {
    return list.map((item) => ({ ...item, contributionPercent: 0 }));
  }

  const weights = list.map((item) => itemWeight(item));
  const raw = weights.map((w) => (w / total) * 100);
  const floors = raw.map((r) => Math.floor(r));
  let remainder = 100 - floors.reduce((sum, n) => sum + n, 0);
  const order = raw
    .map((r, i) => ({ i, frac: r - floors[i] }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  const percents = [...floors];
  for (let k = 0; k < remainder && k < order.length; k += 1) {
    percents[order[k].i] += 1;
  }

  return list.map((item, i) => ({
    ...item,
    contributionPercent: percents[i],
  }));
}

/**
 * Annotate match buckets with contributionPercent (matched + missed = 100%).
 * Missed = boostable + missingHard.
 * @param {MatchScore | null | undefined} match
 * @returns {{
 *   matched: Array<KeywordItem & { contributionPercent: number }>,
 *   missed: Array<KeywordItem & { contributionPercent: number }>,
 *   totalWeight: number,
 * } | null}
 */
export function annotateMatchContributions(match) {
  if (!match) return null;
  const matched = Array.isArray(match.matched) ? match.matched : [];
  const boostable = Array.isArray(match.boostable) ? match.boostable : [];
  const missingHard = Array.isArray(match.missingHard) ? match.missingHard : [];
  const totalWeight =
    Number(match.totalWeight) ||
    [...matched, ...boostable, ...missingHard].reduce((sum, item) => sum + itemWeight(item), 0);

  const annotated = distributeContributionPercents(
    [...matched, ...boostable, ...missingHard],
    totalWeight,
  );
  const nMatched = matched.length;

  return {
    matched: annotated.slice(0, nMatched),
    missed: annotated.slice(nMatched),
    totalWeight,
  };
}

/**
 * @param {number} percent
 * @returns {'weak' | 'medium' | 'strong'}
 */
export function matchBand(percent) {
  if (percent >= 75) return "strong";
  if (percent >= 50) return "medium";
  return "weak";
}

/**
 * @param {number} value
 */
function clampPercent(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

/**
 * @param {KeywordItem[] | null | undefined} resumeKeywords
 * @param {KeywordItem[] | null | undefined} jobKeywords
 * @param {{ evidenceText?: string, matchEvidenceText?: string }} [opts]
 *   evidenceText = source resume prose for honest boost ceiling / franchise.
 *   matchEvidenceText = prose that must contain an exact surface phrase for a
 *   keyword to count as matched (stops stale alias hits). When omitted, matched
 *   is keyword_json ∩ job only (used inside weave loops).
 * @returns {MatchScore | null}
 */
export function computeMatchScore(resumeKeywords, jobKeywords, opts = {}) {
  const resumeIndex = indexByNormalized(resumeKeywords);
  const rawJobList = Array.isArray(jobKeywords) ? jobKeywords : [];
  const jobList = filterItemsByNormalized(
    rawJobList,
    (item) => item?.keyword_normalized || "",
    { collapseSpecific: false },
  );
  const evidenceText = String(opts.evidenceText || "");
  const matchEvidenceText = String(opts.matchEvidenceText || "");

  if (!jobList.length) return null;
  if (!(Array.isArray(resumeKeywords) && resumeKeywords.length) && !matchEvidenceText) {
    return null;
  }

  /** @type {KeywordItem[]} */
  const matched = [];
  /** @type {KeywordItem[]} */
  const missingHard = [];
  /** @type {KeywordItem[]} */
  const boostable = [];

  let totalWeight = 0;
  let matchedWeight = 0;
  let boostableWeight = 0;

  const seenJob = new Set();
  for (const jobKw of jobList) {
    const key = String(jobKw?.keyword_normalized || "")
      .trim()
      .toLowerCase();
    if (!key || seenJob.has(key) || isIgnoredAtsKeyword(key)) continue;
    seenJob.add(key);

    const weight = itemWeight(jobKw);
    totalWeight += weight;

    const resumeMatch = findResumeMatch(resumeIndex, key);
    if (resumeMatch) {
      // Optional exact-phrase gate (UI / final score). Weave loops omit this so
      // newly woven phrases in keywords_json still count while source evidenceText
      // stays frozen for deliverability checks.
      if (
        matchEvidenceText &&
        !resumeHasExactKeywordEvidence(jobKw, matchEvidenceText, resumeMatch)
      ) {
        // Fall through as unmatched (stale/over-broad keyword_json).
      } else {
        matched.push({ ...jobKw, resume_match: resumeMatch });
        matchedWeight += weight;
        continue;
      }
    }

    // Keyword extract/subsume sometimes drops a chip that is still literal in the
    // resume (e.g. Skills: "Accounting"). Count literal surface evidence as matched.
    if (
      matchEvidenceText &&
      resumeHasExactKeywordEvidence(jobKw, matchEvidenceText, null)
    ) {
      matched.push({
        ...jobKw,
        resume_match: {
          keyword: jobKw.keyword,
          keyword_normalized: key,
          category: jobKw.category,
          weight,
          matched_pattern: jobKw.keyword,
          source: "literal_evidence",
        },
      });
      matchedWeight += weight;
      continue;
    }

    if (isWeaveDeliverable(jobKw, evidenceText)) {
      boostable.push(jobKw);
      boostableWeight += weight;
    } else {
      missingHard.push(jobKw);
    }
  }

  if (totalWeight <= 0) return null;

  const currentPercent = clampPercent((matchedWeight / totalWeight) * 100);
  const boostPercent = clampPercent(((matchedWeight + boostableWeight) / totalWeight) * 100);
  const matchedHardCount = matched.filter((item) =>
    SKILLS_CHIP_CATEGORIES.has(normalizeKeywordCategory(item?.category)),
  ).length;

  return {
    currentPercent,
    boostPercent: Math.max(currentPercent, boostPercent),
    band: matchBand(currentPercent),
    matched,
    missingHard,
    boostable,
    totalWeight,
    matchedCount: matched.length,
    matchedHardCount,
    matchedWeight,
    jobKeywordCount: seenJob.size,
  };
}

/**
 * Client-safe score from job keywords + resume plain text (no dictionary / fs).
 * A job keyword counts as matched when its surface phrase appears in resumeText.
 * @param {KeywordItem[] | null | undefined} jobKeywords
 * @param {string} resumeText
 * @returns {MatchScore | null}
 */
export function computeMatchScoreFromEvidence(jobKeywords, resumeText) {
  const text = String(resumeText || "");
  const rawJobList = Array.isArray(jobKeywords) ? jobKeywords : [];
  const jobList = filterItemsByNormalized(
    rawJobList,
    (item) => item?.keyword_normalized || "",
    { collapseSpecific: false },
  );
  if (!jobList.length) return null;

  /** @type {KeywordItem[]} */
  const syntheticResume = [];
  for (const jobKw of jobList) {
    if (resumeHasExactKeywordEvidence(jobKw, text, null)) {
      syntheticResume.push({
        keyword: jobKw.keyword,
        keyword_normalized: jobKw.keyword_normalized,
        category: jobKw.category,
        weight: jobKw.weight,
        matched_pattern: jobKw.keyword,
        source: "literal_evidence",
      });
    }
  }

  return computeMatchScore(syntheticResume, jobList, {
    evidenceText: text,
    matchEvidenceText: text,
  });
}

/**
 * Band colors for the match ring (dashboard).
 * @param {'weak' | 'medium' | 'strong' | null | undefined} band
 */
export function matchBandColor(band) {
  if (band === "strong") return { solid: "#507619", soft: "rgba(80, 118, 25, 0.28)" };
  if (band === "medium") return { solid: "#c47a12", soft: "rgba(196, 122, 18, 0.28)" };
  return { solid: "#c0392b", soft: "rgba(192, 57, 43, 0.28)" };
}
